import { MouseEventHandler } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { differenceInYears } from 'date-fns';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { CustomContactFormContext, useCustomContactFormSlidePanel } from '@frontend/create-contact-panel';
import { useGetDisplayName } from '@frontend/get-display-name';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useThreadHeaderContext } from '@frontend/thread-header-context';
import { ThreadTitle, ThreadTitleProps } from '../thread-title';
import { buildCreateContactContext } from '../utils/create-contact-helper';

type TitleProps = Omit<ThreadTitleProps, 'text' | 'personId' | 'groupId'> & {
  onCreateContact?: CustomContactFormContext['onSave'];
  onCreateContactError?: CustomContactFormContext['onError'];
  isCompactMode?: boolean;
};

const EMPTY_BIRTHDATE = '0001-01-01T00:00:00Z';

export const Title = ({
  onClick,
  openContactPanel = true,
  onCreateContact,
  onCreateContactError,
  variant: providedVariant,
  isCompactMode,
  groupMessagingEnabled,
  participants,
  ...props
}: TitleProps) => {
  const {
    personPhone,
    groupId,
    variant,
    primaryContact,
    primaryContactId,
    contextContact,
    contextPersonId,
    associatedContacts,
  } = useThreadHeaderContext();
  const isSmallMode = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const {
    fullName: primaryDisplayName,
    firstName: primaryDisplayFirstName,
    legalName: primaryLegalName,
  } = useGetDisplayName({
    person: {
      firstName: primaryContact?.firstName || '',
      lastName: primaryContact?.lastName || '',
      preferredName: primaryContact?.preferredName,
    },
  });
  const {
    fullName: contextDisplayName,
    firstName: contextDisplayFirstName,
    legalName: contextLegalName,
  } = useGetDisplayName({
    person: {
      firstName: contextContact?.firstName || '',
      lastName: contextContact?.lastName || '',
      preferredName: contextContact?.preferredName,
    },
  });
  const text = contextContact
    ? contextDisplayName
    : primaryContact
    ? primaryDisplayName
    : formatPhoneNumber(personPhone);
  const { setShow } = useCustomContactFormSlidePanel();

  const getHeadOfHousehold = (contacts: Person[]): string | undefined => {
    if (!Array.isArray(contacts)) return undefined;

    const filteredContacts = contacts.filter(
      (contact): contact is Person & { birthdate: string } =>
        contact.isGuardian === true && !!contact.birthdate && contact.personPmid === contact.householdPmid
    );

    if (filteredContacts.length === 0) {
      return undefined;
    }

    const headOfHousehold = filteredContacts.reduce((oldest, current) =>
      new Date(current.birthdate) < new Date(oldest.birthdate) ? current : oldest
    );

    return headOfHousehold.personId;
  };

  const headOfHouseholdId = getHeadOfHousehold(associatedContacts || []);
  const { updateUnifiedThreadsWithNewPerson } = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();

  const handleDefaultCreateContact: CustomContactFormContext['onSave'] = (res, req) => {
    const allPersonPhones = [req.phone_mobile, req.phone_home, personPhone].filter((phone): phone is string => !!phone);
    updateUnifiedThreadsWithNewPerson({
      personPhones: allPersonPhones,
      firstName: req.first_name,
      lastName: req.last_name,
      preferredName: req.preferred_name ?? '',
      personId: res.data.person_id,
      locationId: groupId,
      onlyEmptyPersons: true,
    });
  };

  const openCreateContact = () => {
    setShow({
      show: true,
      context: buildCreateContactContext({
        firstName: primaryContact?.firstName,
        lastName: primaryContact?.lastName,
        phoneNumber: personPhone,
        locationId: groupId,
        onSave: onCreateContact || handleDefaultCreateContact,
        onError: onCreateContactError,
      }),
    });
  };

  const showGroupThreadTitle = groupMessagingEnabled && !!participants?.length && participants.length > 1;

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    if (showGroupThreadTitle) return;
    if (openContactPanel && !primaryContactId) {
      openCreateContact();
    }
    onClick?.(e);
  };

  const showSecondaryPerson = contextContact && primaryContact && contextContact.personId !== primaryContact?.personId;
  const personForTooltip = contextContact ?? primaryContact;
  const personAge =
    personForTooltip?.birthdate && personForTooltip.birthdate > EMPTY_BIRTHDATE
      ? `${differenceInYears(new Date(), new Date(personForTooltip.birthdate))}`
      : undefined;

  const nameTooltipContent = isSmallMode
    ? [personForTooltip?.gender, personAge].filter(Boolean).join(' • ') || undefined
    : undefined;

  return (
    <ThreadTitle
      css={[openContactPanel && !primaryContactId && { cursor: 'pointer' }, showGroupThreadTitle && { width: '100%' }]}
      {...props}
      groupMessagingEnabled={groupMessagingEnabled}
      participants={participants}
      text={text}
      avatarProps={{
        name: contextContact || primaryContact ? text : undefined,
        firstName: contextContact ? contextDisplayFirstName : primaryDisplayFirstName,
        lastName: contextContact?.lastName ?? primaryContact?.lastName,
        title: contextContact ? contextLegalName : primaryLegalName,
      }}
      personId={contextPersonId || primaryContact?.personId || primaryContactId}
      secondaryPerson={
        showSecondaryPerson
          ? {
              personId: primaryContact.personId,
              firstName: primaryDisplayFirstName,
              preferredName: '',
              lastName: primaryContact.lastName ?? '',
            }
          : undefined
      }
      groupId={groupId}
      variant={providedVariant || variant}
      onClick={handleClick}
      openContactPanel={openContactPanel}
      nameTooltipContent={nameTooltipContent}
      isCompactMode={isCompactMode}
      headOfHouseholdId={associatedContacts && associatedContacts?.length > 1 ? headOfHouseholdId : undefined}
    />
  );
};
